import { ITracker, ITrackerConfig } from './tracker.types';

let __provider: ITracker | null = null;
let __isInitialized = false;
let __isInitializing = false;

/**
 * Stores the API key and config options passed to TrackingFactory.amplitude.
 * Retained at module level so that asyncInitAndUpdateProperties can initialize the provider
 * once a userId is available — autocapture settings can only be applied at init time.
 */
let __apiKey: string | undefined;
let __options: ITrackerConfig | undefined;

/**
 * Buffers the latest userId set via tracker.setUserId if called before the tracking provider is initialized.
 * This ensures that user identification is not lost if setUserId is invoked before Amplitude (or other provider)
 * is ready, which can happen due to async initialization in app bootstrap. The pending userId is flushed
 * automatically after provider setup completes.
 */
let __userId: string | undefined;

/**
 * Buffers user properties passed to tracker.identify before provider initialization.
 * Flushed into the provider alongside the userId inside asyncInitAndUpdateProperties.
 */
let __identity: Record<string, unknown> | undefined;

/**
 * Stable singleton facade used by application code.
 *
 * Tracking is suppressed until both the provider is initialized AND a userId has been set.
 * This prevents events — including autocaptured ones — from being emitted without a user
 * identity, since autocapture can only be configured at provider init time.
 */
export const tracker: ITracker = {
  /**
   * Forwards an event to the provider. No-ops silently if the provider is not yet
   * initialized or if no userId has been set, ensuring no anonymous events are emitted.
   */
  track: (eventName, eventProperties) => {
    if (!__provider || !__userId || !__isInitialized) return;
    __provider.track(eventName, eventProperties);
  },
  identify: identifyInternal,
  setUserId: setUserIdInternal,
  setSessionId: (sessionId) => __provider?.setSessionId(sessionId),
  reset: resetInternal,
};

/**
 * Fully tears down the active provider and clears all module-level state.
 * Called via tracker.reset — ensures a subsequent TrackingFactory.amplitude call
 * starts from a clean slate with no stale userId, identity, or initialization flags.
 */
function resetInternal(): void {
  __provider?.reset();
  __provider = null;
  __userId = undefined;
  __identity = undefined;
  __isInitialized = false;
  __isInitializing = false;
}

/**
 * Buffers user properties so they can be applied after provider initialization.
 * If the provider is already initialized, the call is forwarded immediately;
 * otherwise the properties are held in __identity and flushed inside asyncInitAndUpdateProperties.
 */
function identifyInternal(userProperties: Record<string, unknown>) {
  __identity = {
    ...__identity,
    ...userProperties,
  };
  if (__provider && __isInitialized) {
    __provider.identify(__identity);
  }
}

/**
 * Sets the active user ID and triggers provider initialization if it has not started yet.
 *
 * If the provider is already initialized, the userId (and any buffered identity) are applied
 * immediately. Otherwise the value is stored and asyncInitAndUpdateProperties is called to
 * initialize the provider once both an API key and a userId are available.
 */
function setUserIdInternal(userId?: string): void {
  __userId = userId;
  if (__provider && __isInitialized) {
    __provider.setUserId(userId);
    if (__identity) {
      __provider.identify(__identity);
    }
    return;
  }
  void asyncInitAndUpdateProperties();
}

/**
 * Defers provider initialization until both an API key and a userId are present.
 *
 * Autocapture (e.g. session replay, page-view capture) can only be toggled at init time,
 * so the provider must not be initialized before the userId is known — otherwise events
 * could be captured and sent without a user identity attached.
 *
 * Guard behavior:
 * - Exits immediately if initialization is already in progress or complete.
 * - Exits if either __apiKey or __userId has not been set yet.
 * - On success, flushes the buffered userId and any buffered identity properties into the provider.
 */
async function asyncInitAndUpdateProperties() {
  if (__isInitialized || __isInitializing) return;
  if (!__apiKey || !__userId) return;

  __isInitializing = true;

  const { initAmplitude } = await import('./providers/amplitude');
  __provider = (await initAmplitude(__apiKey, __options)) ?? null;
  __isInitialized = !!__provider;
  __isInitializing = false;

  if (!__provider) return;

  __provider.setUserId(__userId);

  if (__identity) {
    __provider.identify(__identity);
  }
}

/**
 * Initializes and manages the current tracking provider.
 */
export class TrackingFactory {
  /**
   * Stores the Amplitude API key and options, then delegates to asyncInitAndUpdateProperties.
   *
   * Initialization is intentionally deferred until a userId is set — autocapture features
   * (e.g. session replay) can only be configured at init time, so initializing before a user
   * is identified would result in anonymous event capture.
   *
   * Guard behavior:
   * - Disabled config or missing apiKey clears all provider state and exits.
   * - Actual initialization is handled by asyncInitAndUpdateProperties, which guards against
   *   parallel calls and repeated initialization internally.
   */
  static async amplitude(apiKey?: string, options?: ITrackerConfig): Promise<void> {
    if (options?.disabled || !apiKey) {
      __provider = null;
      __isInitialized = false;
      __isInitializing = false;
      __userId = undefined;
      return;
    }

    __apiKey = apiKey;
    __options = options;

    void asyncInitAndUpdateProperties();
  }
}
